import { Intent } from '@blueprintjs/core';
import { type StateObservable } from 'redux-observable';
import { type Observable } from 'rxjs';
import { ignoreElements, tap } from 'rxjs/operators';

import { ActionTypes as AccountsActionTypes } from '../accounts/actions';
import { type Actions as RootActions } from '../actions';
import { type AppState } from '../reducer';
import {
  cancelSubscriptionSuccessMessage,
  graceChangeSuccessMessage,
  logMessage,
  planChangeSuccessMessage,
  trialChangeSuccessMessage,
} from '../slack';

import { Notifications } from './components';

export const processNotifications = (action$: Observable<RootActions>) => {
  let message: string;
  return action$.pipe(
    tap((action: RootActions) => {
      switch (action.type) {
        case AccountsActionTypes.UPDATE_TRIAL_END_FULFILLED:
          Notifications.show({
            intent: Intent.SUCCESS,
            message: 'Updated trial period successfully',
          });
          break;
        case AccountsActionTypes.UPDATE_ENTERPRISE_CAMPAIGN_DATES_FULFILLED:
          Notifications.show({
            intent: Intent.SUCCESS,
            message: 'Updated self-serve Enterprise trial period successfully',
          });
          break;
        case AccountsActionTypes.UPDATE_GUARDIAN_CAMPAIGN_DATES_FULFILLED:
          Notifications.show({
            intent: Intent.SUCCESS,
            message: 'Updated self-serve Guardian trial period successfully',
          });
          break;
        case AccountsActionTypes.UPDATE_GRACE_END_FULFILLED:
          Notifications.show({
            intent: Intent.SUCCESS,
            message: 'Updated grace period successfully',
          });
          break;
        case AccountsActionTypes.UPDATE_TRIAL_END_FAILED:
          Notifications.show({
            intent: Intent.DANGER,
            message: 'Failed to update trial period',
          });
          break;
        case AccountsActionTypes.UPDATE_GRACE_END_FAILED:
          Notifications.show({
            intent: Intent.DANGER,
            message: 'Failed to update grace period',
          });
          break;
        case AccountsActionTypes.UPDATE_ENTERPRISE_CAMPAIGN_DATES_FAILED:
          Notifications.show({
            intent: Intent.DANGER,
            message: 'Failed to update self-serve Enterprise trial period',
          });
          break;
        case AccountsActionTypes.UPDATE_GUARDIAN_CAMPAIGN_DATES_FAILED:
          Notifications.show({
            intent: Intent.DANGER,
            message: 'Failed to update self-serve Guardian trial period',
          });
          break;
        case AccountsActionTypes.UPDATE_LEGACY_TRIAL_END_FULFILLED:
          Notifications.show({
            intent: Intent.SUCCESS,
            message: 'Updated trial period successfully',
          });
          break;
        case AccountsActionTypes.UPDATE_LEGACY_GRACE_END_FULFILLED:
          Notifications.show({
            intent: Intent.SUCCESS,
            message: 'Updated grace period successfully',
          });
          break;
        case AccountsActionTypes.UPDATE_LEGACY_TRIAL_END_FAILED:
          Notifications.show({
            intent: Intent.DANGER,
            message: 'Failed to update trial period',
          });
          break;
        case AccountsActionTypes.UPDATE_LEGACY_GRACE_END_FAILED:
          Notifications.show({
            intent: Intent.DANGER,
            message: 'Failed to update grace period',
          });
          break;
        case AccountsActionTypes.SUBSCRIBE_FULFILLED:
          Notifications.show({
            intent: Intent.SUCCESS,
            message: 'Updated subscription successfully',
          });
          break;
        case AccountsActionTypes.SUBSCRIBE_FAILED:
          // yikes
          message = action.error && action.error.message && (action.error.message as { [key: string]: string }).message;
          Notifications.show({
            intent: Intent.DANGER,
            message: `Failed to update subscription: ${message}`,
          });
          break;
        case AccountsActionTypes.CANCEL_SUBSCRIPTION_FULFILLED:
          Notifications.show({
            intent: Intent.SUCCESS,
            message: 'Canceled subscription successfully',
          });
          break;
        case AccountsActionTypes.CANCEL_SUBSCRIPTION_FAILED:
          message = action.error && action.error.message && (action.error.message as { [key: string]: string }).message;
          Notifications.show({
            intent: Intent.DANGER,
            message: `Failed to update subscription: ${message}`,
          });
          break;
        case AccountsActionTypes.DISABLE_MFA_FAILED:
          message = action.error && action.error.message && (action.error.message as { [key: string]: string }).message;
          Notifications.show({
            intent: Intent.DANGER,
            message: `Failed to disable MFA for account owner: ${message}`,
          });
          break;
        default:
          break;
      }
    }),
    ignoreElements(),
  );
};

export const postToSlack = (action$: Observable<RootActions>, state$: StateObservable<AppState>) =>
  action$.pipe(
    tap((action: RootActions) => {
      const { user } = state$.value.auth;
      const webHookUrl = state$.value.settings.slackWebHookPlanChanges;

      if (!user) {
        return;
      }

      switch (action.type) {
        case AccountsActionTypes.UPDATE_LEGACY_TRIAL_END_FULFILLED:
          logMessage(
            trialChangeSuccessMessage(user, action.account, action.subscription, action.updatedSubscription),
            webHookUrl,
          );
          break;
        case AccountsActionTypes.UPDATE_LEGACY_GRACE_END_FULFILLED:
          logMessage(
            graceChangeSuccessMessage(user, action.account, action.subscription, action.updatedSubscription),
            webHookUrl,
          );
          break;
        case AccountsActionTypes.SUBSCRIBE_FULFILLED:
          logMessage(
            planChangeSuccessMessage(user, action.account, action.subscription, action.updatedSubscription),
            webHookUrl,
          );
          break;
        case AccountsActionTypes.CANCEL_SUBSCRIPTION_FULFILLED:
          logMessage(cancelSubscriptionSuccessMessage(user, action.account), webHookUrl);
          break;
        default:
      }
    }),
    ignoreElements(),
  );
